/**
 * reduce event ของ RTDB tableStatesEvents เป็นสถานะโต๊ะ (pure — เทสต์ใน node ได้: npm run test:table-state)
 *
 * - เรียงตาม ts ก่อนเสมอ (id ตัดสินเมื่อ ts เท่ากัน) — onChildAdded ไม่ได้ส่งมาตามเวลา
 *   event ที่เครื่อง offline ส่งตามมาทีหลังมี ts เก่ากว่าตัวที่มาถึงก่อน
 * - event ของรอบเปิดร้านอื่น (openJobId ไม่ตรง OPEN_SHOP ล่าสุด) ไม่มีผล
 * - CLEAR = โต๊ะกลับเป็นว่าง event ก่อนหน้าของโต๊ะนั้นไม่มีผล
 * - PATCH = ตั้งหลาย field ใน event เดียว (เหมือน SET_GUESTS / SET_NOTE / SET_CHANNEL ตาม field ที่มีใน payload)
 * - ที่เหลือ last-write-wins ต่อ field
 *
 * ผลลัพธ์ shape เดิมทั้งหมด: posStore.tableState (flat) และ jobsStore.tablesInfo (options ที่ใช้คิดเงิน)
 */

import {
  TableStateEventType as T,
  type TableOptionKey,
  type TableOptions,
  type TableOptionValue,
  type TableStateEvent,
  type TableStateValue,
} from "../../../interface/TableStateEvent";

/** outbox ที่ค้างนานกว่านี้ลบทิ้ง ไม่ส่งขึ้น RTDB */
export const OUTBOX_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const OPTION_KEYS: TableOptionKey[] = ["sc", "dc2", "cc", "tax"];

export interface ReducedTable {
  /**
   * flat แบบ tableState เดิม — dc2/cc/tax = options, sc = ของ channel (ถ้ามี) ไม่งั้น options.sc
   * (ลำดับเดียวกับ TablesBuilder: SC ของ channel ทับค่าที่ตั้งเอง)
   */
  state: TableStateValue;
  /** ค่าที่ตั้งเองต่อโต๊ะ (SET_OPTION / RESET_OPTION) — TablesBuilder ใช้คิดเงิน */
  options: TableOptions;
  updatedAt: number;
}

export interface ReduceResult {
  tables: Record<string, ReducedTable>;
  /** ts ของ CLEAR ล่าสุดของแต่ละโต๊ะ (ในรอบปัจจุบัน) */
  clearedAt: Record<string, number>;
}

export const compareEvents = (a: TableStateEvent, b: TableStateEvent) =>
  a.ts - b.ts || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const sortEvents = (events: TableStateEvent[]) => [...events].sort(compareEvents);

/** เครื่องหมายรอบ: OPEN_SHOP (เปิดร้าน) */
export const isSessionMarker = (ev: TableStateEvent | null | undefined) => ev?.type === T.OPEN_SHOP;

/** เครื่องหมายรอบล่าสุด = รอบปัจจุบัน */
export const latestSessionMarker = (events: TableStateEvent[]): TableStateEvent | null => {
  let latest: TableStateEvent | null = null;
  for (const ev of events) {
    if (!isSessionMarker(ev)) continue;
    if (!latest || compareEvents(ev, latest) > 0) latest = ev;
  }
  return latest;
};

const isOptionValue = (value: any): value is TableOptionValue =>
  Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === "number" && !isNaN(n));

const isOptionKey = (key: any): key is TableOptionKey => OPTION_KEYS.includes(key);

/**
 * sc/dc2/cc/tax ใน flat คำนวณตอนท้ายจาก channel + options ไม่ไล่ทับทีละ event
 * sc ของ channel = setServiceChange([0, channel.serviceCharge]) เดิม (เฉพาะค่าที่ไม่ใช่ 0)
 */
const finalize = (table: ReducedTable) => {
  const { state, options } = table;
  for (const key of ["dc2", "cc", "tax"] as TableOptionKey[]) {
    if (options[key]) {
      state[key] = options[key];
    } else {
      delete state[key];
    }
  }
  const channelSc = Number(state.channel?.serviceCharge);
  if (channelSc) {
    state.sc = [0, channelSc];
  } else if (options.sc) {
    state.sc = options.sc;
  } else {
    delete state.sc;
  }
};

const setGuests = (state: TableStateValue, value: any) => {
  const guests = Number(value);
  if (!isNaN(guests)) state.guests = guests;
};

const setNote = (state: TableStateValue, value: any) => {
  if (value) {
    state.note = String(value);
  } else {
    delete state.note;
  }
};

const setChannel = (state: TableStateValue, value: any) => {
  if (value) {
    state.channel = value;
  } else {
    delete state.channel;
  }
};

const inSession = (ev: TableStateEvent, sessionOpenJobId: string | null) =>
  !sessionOpenJobId || !ev.openJobId || ev.openJobId === sessionOpenJobId;

export function reduceTableStates(
  events: TableStateEvent[],
  { sessionOpenJobId }: { sessionOpenJobId: string | null }
): ReduceResult {
  const tables: Record<string, ReducedTable> = {};
  const clearedAt: Record<string, number> = {};

  const tableOf = (name: string): ReducedTable =>
    (tables[name] ??= { state: {}, options: {}, updatedAt: 0 });

  for (const ev of sortEvents(events.filter(Boolean))) {
    if (!inSession(ev, sessionOpenJobId)) continue;

    const payload = ev.payload || {};

    if (!ev.table || ev.type === T.OPEN_SHOP) continue;

    if (ev.type === T.CLEAR) {
      delete tables[ev.table];
      clearedAt[ev.table] = ev.ts;
      continue;
    }

    const table = tableOf(ev.table);
    const { state } = table;
    table.updatedAt = ev.ts;

    switch (ev.type) {
      case T.LOCK:
        state.lock = true;
        break;
      case T.UNLOCK:
        state.lock = false;
        break;
      case T.SET_GUESTS:
        setGuests(state, payload.guests);
        break;
      case T.CLEAR_GUESTS:
        delete state.guests;
        break;
      case T.SET_NOTE:
        setNote(state, payload.note);
        break;
      case T.CLEAR_NOTE:
        delete state.note;
        break;
      case T.SET_CHANNEL:
        setChannel(state, payload.channel);
        break;
      case T.CLEAR_CHANNEL:
        delete state.channel;
        break;
      case T.PATCH:
        // field ที่ไม่มี (RTDB ไม่เก็บ null) = ไม่เปลี่ยน
        if (payload.guests != null) setGuests(state, payload.guests);
        if (payload.note != null) setNote(state, payload.note);
        if (payload.channel != null) setChannel(state, payload.channel);
        break;
      case T.SET_OPTION:
        if (isOptionKey(payload.key) && isOptionValue(payload.value)) {
          table.options[payload.key] = payload.value;
        }
        break;
      case T.RESET_OPTION:
        if (isOptionKey(payload.key)) delete table.options[payload.key];
        break;
    }
  }

  for (const table of Object.values(tables)) finalize(table);
  return { tables, clearedAt };
}

/** posStore.tableState */
export const toTableState = (result: ReduceResult): Record<string, TableStateValue> => {
  const out: Record<string, TableStateValue> = {};
  for (const [name, table] of Object.entries(result.tables)) out[name] = table.state;
  return out;
};

/** jobsStore.tablesInfo — เฉพาะโต๊ะที่ตั้งค่าเอง (โต๊ะอื่นใช้ค่าร้าน) */
export const toTablesInfo = (result: ReduceResult): Record<string, TableOptions> => {
  const out: Record<string, TableOptions> = {};
  for (const [name, table] of Object.entries(result.tables)) {
    if (Object.keys(table.options).length > 0) out[name] = table.options;
  }
  return out;
};

/**
 * event ที่ค้างใน outbox ควรส่งขึ้น RTDB หรือทิ้ง
 * - ค้างนานกว่า 24 ชม. → ทิ้ง
 * - ของรอบเปิดร้านอื่น → ทิ้ง
 * - โต๊ะนั้นมี CLEAR ที่ใหม่กว่าแล้ว → ทิ้ง (ส่งไปก็ไม่มีผล ค้างเป็นขยะใน node)
 */
export function outboxDecision(
  ev: TableStateEvent,
  { now, sessionOpenJobId, clearedAt }: { now: number; sessionOpenJobId: string | null; clearedAt: Record<string, number> }
): "push" | "drop" {
  if (!ev || typeof ev.ts !== "number") return "drop";
  if (now - ev.ts > OUTBOX_MAX_AGE_MS) return "drop";
  if (!inSession(ev, sessionOpenJobId)) return "drop";
  if (ev.table && clearedAt[ev.table] != null && ev.ts < clearedAt[ev.table]) return "drop";
  return "push";
}
